import { getShortTermCarePremiumPerMonth } from '@/features/shop-insurance';

export interface ShortTermCareQuoteFilterCriteria {
  selectedDailyBenefitAmounts: number[];
  /** Monthly ceiling; null means no premium limit. */
  maxMonthlyPremium: number | null;
}

/**
 * Is this row something the advisor can actually place?
 *
 * Rate rows are stored per priced component and the query endpoint composes
 * them into whole quotes. A row with no facility election is a rider component
 * that slipped through: it would render as a $0/day card and throw when
 * selected, since createShortTermCareFundingSource rejects it.
 */
export function isPurchasableShortTermCareQuote(quote: ShortTermCareQuote): boolean {
  return quote.facilityDailyBenefitAmount !== null && quote.facilityBenefitPeriodDays !== null;
}

/**
 * Applies the short-term care view's filters.
 *
 * Pure and exported so the two pieces of non-obvious logic here are testable
 * without rendering: an empty daily-benefit selection must yield NO quotes
 * (the repository reads an empty array as "no filter", so the server would
 * return every amount), and the premium ceiling is compared against a
 * per-month figure rather than the raw modal premium.
 */
export function filterShortTermCareQuotes(
  quotes: ShortTermCareQuote[],
  { selectedDailyBenefitAmounts, maxMonthlyPremium }: ShortTermCareQuoteFilterCriteria,
): ShortTermCareQuote[] {
  return quotes.filter(quote => {
    if (!isPurchasableShortTermCareQuote(quote)) return false;

    if (!selectedDailyBenefitAmounts.includes(Number(quote.facilityDailyBenefitAmount))) {
      return false;
    }

    // Normalised rather than compared raw: the natural rate key includes
    // premiumFrequency, so an annual vintage could coexist with a monthly one
    // and an $840/yr quote must not be excluded by a $100/mo ceiling.
    return maxMonthlyPremium === null || getShortTermCarePremiumPerMonth(quote) <= maxMonthlyPremium;
  });
}
